import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useCart } from '../context/CartContext';
import { API_URL } from '../utils/config';

const Shop = () => {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [sortBy, setSortBy] = useState('default');
  const { addToCart } = useCart();

  useEffect(() => {
    fetchProducts();
  }, []);

  // FIXED: Wrapped in useCallback to fix ESLint warning
  const filterAndSortProducts = useCallback(() => {
    let filtered = [...products];

    // Filter by category
    if (selectedCategory !== 'All') {
      filtered = filtered.filter(product => product.category === selectedCategory);
    }

    // Sort products
    switch (sortBy) {
      case 'price-low':
        filtered.sort((a, b) => a.price - b.price);
        break;
      case 'price-high':
        filtered.sort((a, b) => b.price - a.price);
        break;
      case 'name':
        filtered.sort((a, b) => a.name.localeCompare(b.name));
        break;
      default:
        // Keep default order
        break;
    }

    setFilteredProducts(filtered);
  }, [products, selectedCategory, sortBy]);

  useEffect(() => {
    filterAndSortProducts();
  }, [filterAndSortProducts]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      // FIXED: Changed from single quotes to backticks for template literal
      const response = await axios.get(`${API_URL}/api/products`);
      
      // FIXED: Handle both array and object responses
      const productsData = Array.isArray(response.data) 
        ? response.data 
        : response.data.products || [];
      
      setProducts(productsData);
      setFilteredProducts(productsData);
      setError(null);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching products:', error);
      setError('Failed to load products. Make sure the server is running!');
      // FIXED: Set empty arrays to prevent .map() error
      setProducts([]);
      setFilteredProducts([]);
      setLoading(false);
    }
  };

  // Helper function to get correct image URL
  const getImageUrl = (imagePath) => {
    if (imagePath && imagePath.startsWith('/uploads/')) {
      return `${API_URL}${imagePath}`;
    }
    return imagePath;
  };

  const handleAddToCart = (product, e) => {
    e.preventDefault();
    e.stopPropagation();
    addToCart(product);
    alert(`${product.name} added to cart!`);
  };

  // Get unique categories from products
  const categories = ['All', ...new Set(products.map(p => p.category).filter(Boolean))];

  return (
    <div className="shop">
      {/* Page Header */}
      <section id="page-header">
        <h2>#stayhome</h2>
        <p>Save more with coupons & up to 70% off!</p>
      </section>

      {/* Filter and Sort Section */}
      <section className="filter-section section-p1">
        <div className="filter-container">
          <div className="filter-group">
            <label htmlFor="category">Category:</label>
            <select
              id="category"
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="filter-select"
            >
              {categories.map(category => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label htmlFor="sort">Sort By:</label>
            <select
              id="sort"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="filter-select"
            >
              <option value="default">Default</option>
              <option value="price-low">Price: Low to High</option>
              <option value="price-high">Price: High to Low</option>
              <option value="name">Name: A to Z</option>
            </select>
          </div>

          <div className="products-count">
            Showing {filteredProducts.length} of {products.length} products
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="product1" className="section-p1">
        {loading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading products...</p>
          </div>
        ) : error ? (
          <div className="error-container">
            <p className="error-message">{error}</p>
            <button onClick={fetchProducts} className="retry-btn">
              <i className="fas fa-redo"></i> Retry
            </button>
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="no-products">
            <p>No products found in this category.</p>
            <button 
              onClick={() => setSelectedCategory('All')} 
              className="normal"
            >
              Show All Products
            </button>
          </div>
        ) : (
          <div className="pro-container">
            {filteredProducts.map((product) => (
              <div key={product._id} className="pro">
                <Link to={`/product/${product._id}`}>
                  <img 
                    src={getImageUrl(product.image)} 
                    alt={product.name}
                    onError={(e) => {
                      e.target.src = '/img/products/f1.jpg';
                    }}
                  />
                  <div className="des">
                    <span>{product.brand || 'Goldvines'}</span>
                    <h5>{product.name}</h5>
                    <div className="star">
                      <i className="fas fa-star"></i>
                      <i className="fas fa-star"></i>
                      <i className="fas fa-star"></i>
                      <i className="fas fa-star"></i>
                      <i className="fas fa-star"></i>
                    </div>
                    <h4>{product.price}৳</h4>
                    {product.stock !== undefined && (
                      <p className="stock-info">
                        {product.stock > 0 ? (
                          <span className="in-stock">
                            <i className="fas fa-check-circle"></i> In Stock ({product.stock})
                          </span>
                        ) : (
                          <span className="out-of-stock">
                            <i className="fas fa-times-circle"></i> Out of Stock
                          </span>
                        )}
                      </p>
                    )}
                  </div>
                </Link>
                {/* FIXED: Changed from <a href="#"> to button */}
                <button 
                  className="cart-icon-btn"
                  onClick={(e) => handleAddToCart(product, e)}
                  disabled={product.stock === 0}
                  aria-label="Add to cart"
                >
                  <i className="fa fa-shopping-cart cart"></i>
                </button>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Pagination Section (Optional - for future implementation) */}
      <section id="pagination" className="section-p1">
        <button onClick={() => window.scrollTo(0, 0)}>1</button>
        <button onClick={() => window.scrollTo(0, 0)}>2</button>
        <button onClick={() => window.scrollTo(0, 0)}>
          <i className="fa fa-long-arrow-right"></i>
        </button>
      </section>

      {/* Newsletter Section */}
      <section id="newsletter" className="section-p1 section-m1">
        <div className="newstext">
          <h4>Sign Up For Newsletters</h4>
          <p>Get E-mail updates about our latest shop and <span>special offers.</span></p>
        </div>
        <div className="form">
          <input type="text" placeholder="Your email address" />
          <button className="normal">Sign Up</button>
        </div>
      </section>
    </div>
  );
};

export default Shop;